import { useState } from "react";
import type { ListItemProps } from "@/components/ListItem/ListItem";
import SectionHeading from "@/components/SectionHeading";
import DetailsCard from "@/components/DetailsCard";
import { useErrorBanner } from "@/providers/ErrorBannerProvider";
import { formatGraduationDate, formatHighestLevelOfEducation } from "../../education";
import { canEditApplicationEducationDetails } from "../utils";
import type { ApplicationEducationDetails } from "../queries";
import EducationEditForm from "./EducationEditForm";

interface Props {
  applicationId: string;
  educationDetails: ApplicationEducationDetails | null;
  status: string;
}

function educationOptions(educationDetails: ApplicationEducationDetails | null): ListItemProps[] {
  return [
    {
      iconName: "school",
      overline: "Highest Level of Education",
      headline: formatHighestLevelOfEducation(educationDetails?.highestLevelOfEducation) ?? "--"
    },
    {
      iconName: "today",
      overline: "Graduation Date",
      headline: formatGraduationDate(educationDetails?.graduationMonth, educationDetails?.graduationYear) ?? "--"
    },
    {
      iconName: "description",
      overline: "Name of Institution",
      headline: educationDetails?.schoolName?.trim() || "--"
    }
  ];
}

export default function EducationSection({ applicationId, educationDetails: initialEducationDetails, status }: Props) {
  const [educationDetails, setEducationDetails] = useState(initialEducationDetails);
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const { setError } = useErrorBanner();
  const canEdit = canEditApplicationEducationDetails({ status });

  return (
    <>
      <SectionHeading
        heading="Education History"
        body="View education history, verified via attached proof of education documentation below"
      />
      <div className="w-full min-w-[364px] max-w-[696px]">
        <DetailsCard
          heading="Education"
          options={educationOptions(educationDetails)}
          editingSection={editingSection}
          onEdit={canEdit ? () => setEditingSection("Education") : undefined}
          renderEdit={
            canEdit
              ? () => (
                  <EducationEditForm
                    applicationId={applicationId}
                    educationDetails={educationDetails}
                    onCancel={() => setEditingSection(null)}
                    onSuccess={(updatedEducationDetails) => {
                      setEducationDetails(updatedEducationDetails);
                      setEditingSection(null);
                    }}
                    onError={(message) => {
                      setEditingSection(null);
                      setError(message);
                    }}
                  />
                )
              : undefined
          }
        />
      </div>
    </>
  );
}
